import { useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { ROOMS } from '../rooms';
import { useWorldStore } from '../state/worldStore';
import { roomWalker } from '../state/roomBody';
import { seatPose } from '../services/seats';

// NEARSEATS — chairs that offer themselves as you walk up (2026-11-01, Hype-U).
//
// A seat is only offered once something marks it (RoomMarkers shows "Sit down"
// for each `marks` target, then sitAt + SeatCamera). The café does that by its
// tables (CafeCounter); a room without tables names its chairs instead —
// rooms.js `seats: ['chair_a', …]`, the units the builder made — and this marks
// the nearest few within reach, minus any somebody else holds (sharedSeats),
// plus your own so it keeps its "Stand up". Where a chair is comes from the
// model (services/seats seatPose), measured AT EVERY CHECK: measured once, on
// the first frame, it was read before the room had been fitted and placed, and
// no chair was ever near you (2026-11-01, Hype-U's classroom: "I cannot choose a
// chair"). Six boxes four times a second costs nothing.
const SIT_NEAR = 1.4;    // world units
const SIT_MAX = 3;
const EVERY_S = 0.25;

export default function NearSeats({ roomId, model }) {
  const camera = useThree((s) => s.camera);
  const marked = useRef('');
  const acc = useRef(0);

  useFrame((_, dt) => {
    acc.current += dt;
    if (acc.current < EVERY_S || !model) return;
    acc.current = 0;
    const room = ROOMS[roomId];
    const names = room?.seats || [];
    if (!names.length) return;
    model.updateWorldMatrix(true, true);
    const spots = names.map((name) => {
      const pose = seatPose(model.getObjectByName(name));
      return pose ? { name, x: pose.pos[0], z: pose.pos[2] } : null;
    }).filter(Boolean);
    const ws = useWorldStore.getState();
    if (ws.seated && ws.seated.phase !== 'seated') return;   // sitting down or standing up: the seat camera has it
    const mine = ws.seated?.target || null;
    const now = Date.now();
    const holds = room.sharedSeats && ws.roomSeats?.zone === roomId ? ws.roomSeats.seats : null;
    const taken = (name) => { const h = holds?.[name]; return !!h && !h.mine && h.until > now; };
    const p = roomWalker(camera);
    const near = spots
      .filter((s) => !taken(s.name))
      .map((s) => ({ name: s.name, d: Math.hypot(s.x - p.x, s.z - p.z) }))
      .filter((s) => s.d < SIT_NEAR)
      .sort((a, b) => a.d - b.d)
      .slice(0, SIT_MAX)
      .map((s) => s.name);
    if (mine && !near.includes(mine)) near.unshift(mine);
    const key = near.join('|');
    if (key !== marked.current) {
      marked.current = key;
      ws.setMarks(near.map((target) => ({ target })));
      if (near.length) console.log(`[seats] ${roomId}: offering ${near.join(', ')}`);
    }
  });
  return null;
}

